import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import type { SharedMobile } from '@/types/institute';

export interface SharedMobilesCheckProps {
  sharedMobiles: SharedMobile[];
  /** The office has checked the numbers: send the same file again and admit it. */
  onConfirm: () => void;
  /** Leave it for now — usually to correct the sheet and upload it again. */
  onCancel: () => void;
  isImporting: boolean;
}

/**
 * The parent mobiles a sheet would give to more than one student, held up for
 * the office to check before anybody is admitted.
 *
 * A question, not a rejection: brothers and sisters share their parents'
 * number, so the office can say "that is right" and carry on. Every number is
 * listed with the sheet's own row numbers, so a value pasted down a column by
 * mistake is found in the file rather than guessed at.
 */
export function SharedMobilesCheck({ sharedMobiles, onConfirm, onCancel, isImporting }: SharedMobilesCheckProps) {
  return (
    <div className="flex flex-col gap-3.5 border-t border-line pt-4">
      <Alert
        tone="warning"
        title={
          sharedMobiles.length === 1
            ? 'One mobile number is shared between students'
            : `${sharedMobiles.length} mobile numbers are shared between students`
        }
      >
        Nobody has been admitted yet. Brothers and sisters often share a parent's number — if
        these are right, import the sheet as it is. If not, correct the rows and upload the file
        again.
      </Alert>

      <ul className="flex flex-col gap-2" aria-label="Shared mobile numbers">
        {sharedMobiles.map((shared) => (
          <li key={`${shared.mobile}-${shared.rows[0]?.row ?? 0}`} className="card flex flex-col gap-1.5 p-3.5">
            <p className="text-sm text-ink">
              This mobile number (<span className="font-mono">{shared.mobile}</span>) is associated
              with <strong>{shared.studentCount} students</strong>. Please verify whether this is
              correct.
            </p>
            <p className="text-xs text-ink-2">
              {shared.rows.map((row) => `Row ${row.row} · ${row.name}`).join(', ')}
              {shared.onRoll > 0 &&
                `${shared.rows.length > 0 ? ', and ' : ''}${shared.onRoll} ${
                  shared.onRoll === 1 ? 'student' : 'students'
                } already on the roll`}
            </p>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={isImporting}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={onConfirm} isLoading={isImporting}>
          The numbers are correct — import
        </Button>
      </div>
    </div>
  );
}
